import { cloneDeep, isEmpty, pick } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import FilterItem from "~/components/Items/FilterItem";
import Radio from "~/components/Radio";
import RadioGroup from "~/components/Radio/RadioGroup";
import RadioCustom from "./radio-custom";

export const anyPriceValue = 'any_price'

type PriceRangeValues = {
  from?: number
  to?: number
}

enum DefaultOptionKey {
  AnyPrice = 'any_price',
  Under = 'under',
  Over = 'over',
}

type Value = {
  label: string
  value?: string
}

type OptionValues = {
  [DefaultOptionKey.AnyPrice]: Value
  [DefaultOptionKey.Under]: Value
  [DefaultOptionKey.Over]: Value
}

const defaultOptions: OptionValues = {
  [DefaultOptionKey.AnyPrice]: {
    label: 'Any price',
    value: anyPriceValue,
  },
  [DefaultOptionKey.Under]: {
    label: '',
    value: '',
  },
  [DefaultOptionKey.Over]: {
    label: '',
    value: '',
  },
}

const defaultOption = [
  {
    "label": "$25-$50",
    "value": "25_50"
  },
  {
    "label": "$50-$100",
    "value": "50_100"
  },
]

const PriceFilter: FC<{
  onChange: (priceRange: PriceRangeValues) => void;
  aggregations?: Aggregation;
  values?: PriceRangeValues;
}> = ({ onChange, aggregations, values }) => {
  const options = defaultOption as Partial<OptionAggregation>[]

  const initFromTo = (values?.from !== undefined && values?.to !== undefined) ? `${values?.from}_${values?.to}` : defaultOptions['any_price'].value
  const [defaultValue] = useState(() => initFromTo)

  const [optionValues, setOptionValues] = useState<OptionValues>(defaultOptions)
  const [radioValue, setRadioValue] = useState<OptionAggregation['value'] | undefined>(() => initFromTo);
  const [custom, setCustom] = useState<{ checked?: boolean, value?: string }>({ checked: false, value: '' })

  const handleChangeRadio = (value?: OptionAggregation['value']) => {
    setRadioValue(value)
    if (!value) return
    const [from, to] = value?.split("_")?.map(Number) || []
    const result = { from, to };
    onChange(anyPriceValue === value ? { from: undefined, to: undefined } : result);
  }

  const customOptions = useMemo(() => {
    if (isEmpty(options)) return []
    const onConvertToArr = (obj: object) => {
      return Object.entries(obj).map(([key, value]) => {
        return {
          ...value,
        }
      })
    }
    const startOrderOptionValues = onConvertToArr(pick(optionValues, [DefaultOptionKey.AnyPrice, DefaultOptionKey.Under]))
    const lastOrderOptionValues = onConvertToArr({ ...pick(optionValues, [DefaultOptionKey.Over]) })
    const concatValues = [...startOrderOptionValues, ...options, ...lastOrderOptionValues].filter((o) => !!o?.label)

    return concatValues

  }, [options, optionValues])

  useEffect(() => {
    if (isEmpty(options)) return
    const cloneOptions = cloneDeep(options)
    const underMinValue = cloneOptions[0]?.value?.split("_")?.[0]
    const overValue = cloneOptions[cloneOptions.length - 1]?.value?.split("_")?.[1]
    const underOption = {
      label: `Under $${underMinValue}`,
      value: `0_${underMinValue}`
    }
    const overOption = {
      label: `Over $${overValue}`,
      value: `${overValue}_200`
    }
    const cloneOptionValues = cloneDeep(optionValues)
    if (Number(underMinValue) !== 0) {
      cloneOptionValues[DefaultOptionKey.Under] = underOption
    }

    if (Number(overValue) < 200) {
      cloneOptionValues[DefaultOptionKey.Over] = overOption
    }

    setOptionValues(cloneOptionValues)
  }, [options])

  useEffect(() => {
    if (custom?.checked) {
      handleChangeRadio(custom?.value)
    }
  }, [custom])

  useEffect(() => {
    const noActive = isEmpty(customOptions) ? false : !customOptions.map((o) => o?.value).includes(defaultValue)
    if (noActive) {
      setCustom({ checked: true, value: defaultValue })
    } else {
      setCustom({ checked: false, value: undefined })
    }
  }, [options, customOptions, defaultValue])

  return (
    <FilterItem title="Price">
      <RadioGroup>
        {
          customOptions.map((o, index: number) => {
            const v = o?.value
            return <span
              key={index}
              className="block"
            >
              <Radio
                checked={!!custom?.checked ? (radioValue !== custom.value && radioValue === v) : radioValue === v}
                id={v}
                rootClick={() => {
                  setCustom(prev => ({ ...prev, checked: false }))
                  handleChangeRadio(v)
                }}
                rootClassName="inline-flex"
              >
                {o.label}
              </Radio>
            </span>
          })}
        {(!isEmpty(customOptions)) &&
          <span className="block">
            <Radio
              checked={!!custom?.checked}
              rootClick={() => setCustom(prev => ({ ...prev, checked: true }))}
              rootClassName="inline-flex"
            >
              Custom
            </Radio>
            <RadioCustom
              onChange={(priceRange) => setCustom({ checked: true, value: priceRange })}
              value={custom?.value}
            />
          </span>
        }
      </RadioGroup>
    </FilterItem>
  );
};

export default PriceFilter;
